/**
 * ConnectedApps.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Shows AI apps (ChatGPT, Gemini, Claude) that the user has authorized via
 * OAuth to access their Lusuku account. Users can revoke access here.
 *
 * Rendered inside the MyProfile sidebar at /my_profile/connected_apps.
 */

import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import LinkIcon from "@material-ui/icons/Link";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import SecurityIcon from "@material-ui/icons/Security";
import SearchIcon from "@material-ui/icons/Search";
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import { BounceLoader } from "react-spinners";
import PageLayout from "../PageLayout/PageLayout";
import { publicRequest } from "../../api/requestMethods";
import { CURRENCY_LOCALE } from "../../config/market";

import "./ConnectedApps.css";

// ── Scope icons + labels ─────────────────────────────────────────────────────

const SCOPE_META = {
  "catalog:read":  { icon: <SearchIcon style={{ fontSize: 14, color: "#6366f1" }} />, label: "Search products & vendors" },
  "orders:write":  { icon: <ShoppingCartIcon style={{ fontSize: 14, color: "#16a34a" }} />, label: "Create orders" },
  "orders:read":   { icon: <LocalShippingIcon style={{ fontSize: 14, color: "#2563eb" }} />, label: "View order history" },
  "profile:read":  { icon: <PersonOutlineIcon style={{ fontSize: 14, color: "#9333ea" }} />, label: "View your name & phone" },
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(dateStr) {
  if (!dateStr) return "Never";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins  = Math.floor(diff / 60000);
  if (mins < 1)    return "Just now";
  if (mins < 60)   return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24)  return `${hours}h ago`;
  const days  = Math.floor(hours / 24);
  if (days < 30)   return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString(CURRENCY_LOCALE, { year: "numeric", month: "short", day: "numeric" });
}

// ── App logo with graceful fallback ─────────────────────────────────────────
// A broken/missing logoUrl (e.g. ChatGPT's hot-linked icon failing to load)
// previously rendered the browser's broken-image glyph. Fall back to the
// first-letter placeholder on load error instead.

const AppLogo = ({ app }) => {
  const [failed, setFailed] = useState(false);
  if (app.logoUrl && !failed) {
    return (
      <img
        src={app.logoUrl}
        alt={app.clientName}
        className="ca-logo"
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <div className="ca-logo-placeholder">{app.clientName?.[0] || "A"}</div>
  );
};

// ── Main component ──────────────────────────────────────────────────────────

const ConnectedApps = () => {
  const user = useSelector((state) => state.user.currentUser);
  const token = user?.accessToken || user?.result?.accessToken;

  const [apps, setApps]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [revoking, setRevoking] = useState(null); // grantId being revoked

  const fetchApps = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const res = await publicRequest.get("/oauth/connected-apps", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setApps(res.data.apps || []);
    } catch (err) {
      console.error("[ConnectedApps] fetch error:", err);
      toast.error("Failed to load connected apps.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchApps(); }, [fetchApps]);

  const handleRevoke = async (app) => {
    const confirmed = window.confirm(
      `Disconnect ${app.clientName}?\n\nThis will immediately revoke its access to your Lusuku account. You can reconnect it later by authorizing again.`
    );
    if (!confirmed) return;

    try {
      setRevoking(app._id);
      await publicRequest.delete(`/oauth/connected-apps/${app._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${app.clientName} has been disconnected.`);
      setApps((prev) => prev.filter((a) => a._id !== app._id));
    } catch (err) {
      console.error("[ConnectedApps] revoke error:", err);
      toast.error("Failed to disconnect app. Please try again.");
    } finally {
      setRevoking(null);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <PageLayout
      icon={LinkIcon}
      title="Connected Apps"
      subtitle="AI assistants that can access your Lusuku account"
    >
      {loading ? (
        <div className="ca-loader">
          <BounceLoader size={40} color="#078C03" />
        </div>
      ) : apps.length === 0 ? (
        <div className="ca-empty">
          <SecurityIcon style={{ fontSize: 48, color: "#d1d5db" }} />
          <h3 className="ca-empty-title">No connected apps</h3>
          <p className="ca-empty-text">
            When you authorize an AI assistant (like ChatGPT or Gemini) to shop
            on Lusuku on your behalf, it will appear here. You can revoke access
            at any time.
          </p>
        </div>
      ) : (
        <div className="ca-list">
          {/* Security banner */}
          <div className="ca-banner">
            <SecurityIcon style={{ fontSize: 18, color: "#16a34a" }} />
            <span>
              These apps can only perform actions you've approved. They never see
              your password. Revoke access anytime.
            </span>
          </div>

          {apps.map((app) => (
            <div key={app._id} className="ca-card">
              <div className="ca-card-header">
                <AppLogo app={app} />
                <div className="ca-card-info">
                  <h4 className="ca-card-name">{app.clientName}</h4>
                  <span className="ca-card-meta">
                    Connected {timeAgo(app.grantedAt)} · Last used {timeAgo(app.lastUsedAt)}
                  </span>
                </div>
                <button
                  className="ca-revoke-btn"
                  onClick={() => handleRevoke(app)}
                  disabled={revoking === app._id}
                  title={`Disconnect ${app.clientName}`}
                >
                  {revoking === app._id ? (
                    <BounceLoader size={14} color="#dc2626" />
                  ) : (
                    <DeleteOutlineIcon style={{ fontSize: 18 }} />
                  )}
                  <span>{revoking === app._id ? "Revoking…" : "Disconnect"}</span>
                </button>
              </div>

              {/* Scope chips */}
              <div className="ca-scopes">
                {(app.scope || []).map((s) => {
                  const meta = SCOPE_META[s] || { icon: <CheckCircleOutlineIcon style={{ fontSize: 14, color: "#16a34a" }} />, label: s };
                  return (
                    <span key={s} className="ca-scope-chip">
                      <span className="ca-scope-icon">{meta.icon}</span>
                      {meta.label}
                    </span>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </PageLayout>
  );
};

export default ConnectedApps;
